import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Tooltip, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { MoneyInputv3 } from '../formElements/moneyFormatInput';
import { MoneyFormatv3 } from '../../library/MoneyFormat';
import ActionButtons from '../Buttons/ActionButtons';

import './openingbalancerow.css';
import { handleFreeze } from '../../utils/common';
import { MoneyTextField } from '../_components';

// SVG Icon Component
const ExpandIcon = React.memo(({ isExpanded }) =>
  isExpanded ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="12.033" height="6" viewBox="0 0 10.033 5">
      <polygon points="5.016 0 0 0.003 2.506 2.5 5.016 5 7.525 2.5 10.033 0.003 5.016 0" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="6" height="12.033" viewBox="0 0 5 10.033">
      <polygon points="0 5.016 0.003 10.033 2.5 7.525 5 5.016 2.5 2.506 0.003 0 0 5.016" />
    </svg>
  )
);

function OpeningBalanceRecursiveRow({
  row,
  expandedRows,
  toggleExpandRow,
  newLedgerResponse,
  updateLedger,
  setAccountID,
  setInputFieldRef,
  handleSelectRow,
  setStockManagement,
  theme,
  inputDisabled = false,
}) {
  const navigate = useNavigate();
  const { saveSkipStatus } = useSelector((state) => state.companySipSaveReducer);

  const isSmallDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const matches = useMediaQuery('(max-width:750px)');
  const multiplyWith = isSmallDevice ? 5 : 10;
  const level = row.depth * multiplyWith;
  const isExpanded = expandedRows.includes(row.id);

  const [debitValue, setDebitValue] = useState(row?.debit);
  const [creditValue, setCreditValue] = useState(row?.credit);

  const debitInputRef = useRef(null);
  const creditInputRef = useRef(null);

  const debitInputId = `debit-${row.id}`;
  const creditInputId = `credit-${row.id}`;

  useEffect(() => {
    setDebitValue(row?.debit);
    setCreditValue(row?.credit);
  }, [row?.debit, row?.credit]);

  const focusAndScroll = useCallback(
    (element) => {
      if (element) {
        setInputFieldRef(element);
        element.focus();
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    [setInputFieldRef]
  );

  const Freeze = useSelector((state) => state.userPrefrenceandReducer?.financialYear?.freezestatus);

  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      setInputFieldRef(e.target);
      e.target.focus();
    },
    [setInputFieldRef]
  );

  const handleChangeAmount = useCallback(
    (type, value, id) => {
      console.log({ value });

      if (!saveSkipStatus) {
        navigate('/companysetup');
        return;
      }

      if (type === 'debit') {
        setDebitValue(value);
        setCreditValue(0);
      } else {
        setCreditValue(value);
        setDebitValue(0);
      }

      updateLedger(id, type, value);
    },
    [saveSkipStatus, navigate, updateLedger]
  );

  const handleKeyDown = useCallback(
    (e, currentInputId, nextInputId) => {
      const allInputs = Array.from(document.querySelectorAll("[id^='debit-'], [id^='credit-']"));
      const currentIndex = allInputs.findIndex((input) => input.id === currentInputId);

      if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        const nextEl = nextInputId ? document.getElementById(nextInputId) : allInputs[currentIndex + 1];
        focusAndScroll(nextEl);
      } else if (e.key === 'Tab' && e.shiftKey) {
        e.preventDefault();
        const prevEl = allInputs[currentIndex - 1];
        focusAndScroll(prevEl);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextEl = allInputs[currentIndex + 2] || allInputs[currentIndex + 1];
        focusAndScroll(nextEl);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevEl = allInputs[currentIndex - 2] || allInputs[currentIndex - 1];
        focusAndScroll(prevEl);
      }
    },
    [focusAndScroll]
  );

  const isGroup = row.category === 'group';

  const textColor = useMemo(() => {
    if (row.category === 'label') return '#818694';
    if (row.category === 'group') return theme.palette.primary.main;
    if (row.name === 'Net Loss') return '#d40217';
    return theme.palette.TmId.id.toString().includes('9') ? '#fff' : '#000000';
  }, [row.category, row.name, theme.palette.primary.main, theme.palette.TmId.id]);

  const borderColor = useMemo(() => {
    return theme.palette.TmId.id.toString().includes('9') ? '#26262F' : '#EEEEFA';
  }, [theme.palette.TmId.id]);

  const renderItemWise = useMemo(() => {
    if (row.id !== 24) return null;
    
    const handleClick = () => {
      setStockManagement({
        isModalOpen: true,
        credit: creditValue,
        debit: debitValue,
        accountid: row.id,
      });
    };

    const buttonElement = matches ? (
      <p
        className="enter-item-label"
        onClick={handleClick}
      >
        Enter item wise
      </p>
    ) : (
      <button
        className="outlined-btn small-btn"
        onClick={handleClick}
      >
        Enter item wise
      </button>
    );

   return buttonElement;
  }, [row.id, matches, textColor, setStockManagement, inputDisabled, creditValue, debitValue]);

  return (
    <div key={row._id} style={{ display: 'flex' }}>
      <div className="custom-box" style={{ '--border-color': borderColor }} data-row-id={row.id}>
        <div className="stack-row">
          <div
            className="content-box"
            style={{
              paddingLeft: `${level}px`,
              cursor: row.category === 'ledger' ? 'pointer' : 'default',
              color: textColor,
              fontWeight: row.category !== 'ledger' ? 'bold' : 'normal',
            }}
            onClick={() => row.category === 'ledger' && setAccountID(row.id)}
          >
            <button className="table-icon" onClick={() => toggleExpandRow(row.id)}>
              {isGroup ? <ExpandIcon isExpanded={isExpanded} /> : <div className="circle-icon"></div>}
            </button>
            <Tooltip title={row.name}>{row.name}</Tooltip>
            {/* New label next to ledger name */}
            {newLedgerResponse === (row.category === 'ledger' ? row._id : row.id) && <div className="new-label">New</div>}
            {/* Only show Default A/C text inside content-box for non-Opening Stock rows */}
            {row.id !== 24 && (
              <span className={`hover-default italic-text ${row.restrictedflag === 1 ? '' : 'hidden'}`}>
                {row.category === 'ledger' ? 'Default A/C' : 'Default Group'}
              </span>
            )}
          </div>

          {renderItemWise}

          {/* Only show Default A/C text after button for Opening Stock row */}
          {row.id === 24 && (
            <span className={`hover-default italic-text ${row.restrictedflag === 1 ? '' : 'hidden'}`}>
              {row.category === 'ledger' ? 'Default A/C' : 'Default Group'}
            </span>
          )}
        </div>

        <div style={{ flexShrink: 0 }}>
          <ActionButtons
            item={row}
            handleSelectRow={handleSelectRow}
            theme={theme}
            bgcolor={theme.palette.primary.main}
            page="OpeningBalance"
          />
        </div>
      </div>

      {/* Debit */}
      <div className="debit-box" style={{ '--border-color': borderColor }}>
        <div className="debit-box-inner">
          {isGroup ? (
            <p className="debit-text" style={{ color: textColor }}>
              ₹ {MoneyFormatv3(row.debit)}
            </p>
          ) : row.id === 24 ? (
            <Tooltip 
              title="This field is automatically updated when you enter item-wise stock details using the 'Enter item wise' button above"
              placement="top"
              arrow
            >
              <div style={{ width: '100%' }}>
                <MoneyTextField
                  id={debitInputId}
                  state={debitValue}
                  value={debitValue}
                  disabled={true}
                  onClick={handleClick}
                  onChange={(e) => {
                    if (Freeze === 1 || inputDisabled || row.id === 24) return;
                    handleChangeAmount('debit', e, row.id);
                  }}
                  onKeyDown={(e) => handleKeyDown(e, debitInputId, creditInputId)}
                  inputRef={debitInputRef}
                  variant="outlined"
                  sx={{ 
                    width: '100%',
                    height: '40px',
                    margin: '4px 0',
                    '& .MuiOutlinedInput-root': {
                      height: '40px'
                    }
                  }}
                />
              </div>
            </Tooltip>
          ) : (
            <MoneyTextField
              id={debitInputId}
              state={debitValue}
              value={debitValue}
              disabled={Freeze === 1 || inputDisabled}
              onClick={handleClick}
              onChange={(e) => {
                if (Freeze === 1 || inputDisabled) return;
                handleChangeAmount('debit', e, row.id);
              }}
              onKeyDown={(e) => handleKeyDown(e, debitInputId, creditInputId)}
              inputRef={debitInputRef}
              variant="outlined"
              sx={{ 
                width: '100%',
                maxWidth: '120px',
                '& .MuiOutlinedInput-root': {
                  height: '36px'
                }
              }}
            />
          )}
        </div>
      </div>

      {/* Credit */}
      <div className="credit-box">
        <div className="credit-box-inner">
          {isGroup ? (
            <span className="credit-text" style={{ color: textColor }}>
              ₹ {MoneyFormatv3(row.credit)}
            </span>
          ) : row.id === 24 ? (
            <Tooltip 
              title="This field is automatically updated when you enter item-wise stock details using the 'Enter item wise' button above"
              placement="top"
              arrow
            >
              <div style={{ width: '100%' }}>
                <MoneyTextField
                  id={creditInputId}
                  state={creditValue}
                  value={creditValue}
                  disabled={true}
                  onClick={handleClick}
                  onChange={(e) => {
                    if (Freeze === 1 || inputDisabled || row.id === 24) return;
                    handleChangeAmount('credit', e, row.id);
                  }}
                  onKeyDown={(e) => handleKeyDown(e, creditInputId, null)}
                  inputRef={creditInputRef}
                  variant="outlined"
                  sx={{ 
                    width: '100%',
                    height: '40px',
                    margin: '4px 0',
                    '& .MuiOutlinedInput-root': {
                      height: '40px'
                    }
                  }}
                />
              </div>
            </Tooltip>
          ) : (
            <MoneyTextField
              id={creditInputId}
              state={creditValue}
              value={creditValue}
              disabled={Freeze === 1 || inputDisabled}
              onClick={handleClick}
              onChange={(e) => {
                if (Freeze === 1 || inputDisabled) return;
                handleChangeAmount('credit', e, row.id);
              }}
              onKeyDown={(e) => handleKeyDown(e, creditInputId, null)}
              inputRef={creditInputRef}
              variant="outlined"
              sx={{ 
                width: '100%',
                maxWidth: '120px',
                '& .MuiOutlinedInput-root': {
                  height: '36px'
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default React.memo(OpeningBalanceRecursiveRow, (prev, next) => {
  return (
    prev.row.id === next.row.id &&
    prev.row.name === next.row.name &&
    prev.row.category === next.row.category &&
    prev.row.debit === next.row.debit &&
    prev.row.credit === next.row.credit &&
    prev.expandedRows.join(',') === next.expandedRows.join(',') &&
    prev.newLedgerResponse === next.newLedgerResponse &&
    prev.inputDisabled === next.inputDisabled
  );
});
